import React from 'react';
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import ListItemContent from './ListItemContent';
import ListItemMark from './ListItemMark';
import Typography from '../Typography';
import { useThemeTokens } from '../ThemeProvider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectItemBlockStyles = _ref => {
  let {
    interItemMargin
  } = _ref;
  return {
    marginBottom: interItemMargin
  };
};
const selectDividerStyles = _ref2 => {
  let {
    dividerColor,
    dividerSize,
    interItemMarginWithDivider
  } = _ref2;
  return {
    borderBottomWidth: dividerSize,
    borderColor: dividerColor,
    marginBottom: interItemMarginWithDivider,
    paddingBottom: interItemMarginWithDivider
  };
};

/**
 * ListItem is responsible for rendering icon or a bullet as side item
 */
const ListItemBase = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    tokens,
    icon,
    iconColor,
    iconSize,
    showDivider,
    children,
    title,
    isLastItem,
    accessibilityRole = Platform.select({
      web: 'listitem',
      default: undefined
    }),
    ...rest
  } = _ref3;
  const themeTokens = typeof tokens === 'function' ? tokens() : tokens;
  const itemBlockStyles = selectItemBlockStyles(themeTokens);
  const dividerStyles = selectDividerStyles(themeTokens);
  const {
    iconMarginTop,
    itemIconSize
  } = themeTokens;
  let adjustedIconMarginTop = iconMarginTop;
  const {
    fontSize,
    lineHeight: lineHeightRatio
  } = useThemeTokens('Typography', {}, {
    size: 'h4',
    bold: true
  });
  if (title) {
    adjustedIconMarginTop = (fontSize * lineHeightRatio - itemIconSize) / 2;
  }
  /**
   * Function responsible returning styling, in case the item is the last shouldn't
   * add extra margin on the bottom, if "showDivider" is true it should add a divider
   * and custom margin and padding, otherwise just adds a margin to the bottom
   */
  const getContainerStyle = () => {
    if (isLastItem) {
      return undefined;
    }
    if (showDivider) {
      return dividerStyles;
    }
    return itemBlockStyles;
  };
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: [staticStyles.container, getContainerStyle()],
    accessibilityRole: accessibilityRole,
    ...selectProps(rest),
    children: typeof children === 'function' ? children({
      tokens,
      icon,
      iconColor,
      iconSize,
      isLastItem
    }) : /*#__PURE__*/_jsxs(View, {
      style: staticStyles.container,
      children: [/*#__PURE__*/_jsx(ListItemMark, {
        tokens: {
          ...tokens,
          iconMarginTop: adjustedIconMarginTop
        },
        icon: icon,
        iconColor: iconColor,
        iconSize: iconSize
      }), /*#__PURE__*/_jsxs(View, {
        style: staticStyles.titleAndContentContainer,
        children: [Boolean(title) && /*#__PURE__*/_jsx(Typography, {
          variant: {
            size: 'h4',
            bold: true
          },
          children: title
        }), /*#__PURE__*/_jsx(ListItemContent, {
          tokens: tokens,
          children: children
        })]
      })]
    })
  });
});
ListItemBase.displayName = 'ListItem';
const staticStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row'
  },
  titleAndContentContainer: {
    flexDirection: 'column',
    flexShrink: 1,
    flexGrow: 1
  }
});
ListItemBase.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('List'),
  variant: variantProp.propType,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * Renders side item icon
   */
  icon: PropTypes.elementType,
  /**
   * Will set display icon color
   */
  iconColor: PropTypes.string,
  /**
   * Allow the user define the icon size if not defined the theme's file
   */
  iconSize: PropTypes.number,
  /**
   * @ignore
   * Defined by parent if it's last item on the list
   */
  isLastItem: PropTypes.bool,
  /**
   * @ignore
   * In case it is not the last item allow display divider
   */
  showDivider: PropTypes.bool
};
export default ListItemBase;